// Explain content mixes prose with LaTeX, and two features have to treat
// notation as opaque: sanitizing (a `<` inside math is not a tag) and sentence
// splitting (a period inside `$3.5$` is not a sentence end). Both walk this one
// segment list so they can never disagree about where math starts and ends.

// The delimiter pairs mathpix-markdown-it configures MathJax with (its
// mathJaxConfig: inlineMath `$…$` and `\(…\)`, displayMath `$$…$$` and `\[…\]`).
// Longest opener first, so `$$` is never read as two empty `$` spans.
const MATH_DELIMITERS = [
  { open: '$$', close: '$$' },
  { open: '\\[', close: '\\]' },
  { open: '\\(', close: '\\)' },
  { open: '$', close: '$' }
]

const openerAt = (text, index) => {
  for (const candidate of MATH_DELIMITERS) {
    if (text.startsWith(candidate.open, index)) return candidate
  }
  return null
}

/**
 * Splits text into ordered `{ type: 'text' | 'math', value }` segments, where a
 * math segment keeps its own delimiters. Joining every `value` back together
 * reproduces the input exactly.
 *
 * An unclosed opener is prose: MathJax only treats a delimiter as math once it
 * finds its partner, so `Costs $5 to make` stays a sentence about money.
 */
export const splitMathSegments = (input) => {
  const text = typeof input === 'string' ? input : ''
  const segments = []
  let plainStart = 0
  let index = 0

  const pushText = (end) => {
    if (end > plainStart) {
      segments.push({ type: 'text', value: text.slice(plainStart, end) })
    }
  }

  while (index < text.length) {
    // `processEscapes` is on, so `\$` is a literal dollar sign, not an opener.
    if (text[index] === '\\' && text[index + 1] === '$') {
      index += 2
      continue
    }

    const delimiter = openerAt(text, index)
    if (!delimiter) {
      index += 1
      continue
    }

    const contentStart = index + delimiter.open.length
    const closeIndex = text.indexOf(delimiter.close, contentStart)
    if (closeIndex === -1) {
      index = contentStart
      continue
    }

    pushText(index)
    const end = closeIndex + delimiter.close.length
    segments.push({ type: 'math', value: text.slice(index, end) })
    plainStart = end
    index = end
  }

  pushText(text.length)
  return segments
}
